import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import styles from "./ChoiceMenu.module.css";

// ChoiceMenu (C06/C08 del catálogo): mismo HTML que el componente de Bheli.Web.Feedback. El menú es un popover nativo
// anclado con CSS (position-anchor), igual que en el portal actual: misma posición, mismo foco y mismas teclas.

export type ChoiceOption = { value: string; label: string; disabled?: boolean };

/** Con más de 6 opciones (o mientras quede texto escrito) aparece el buscador, si quien lo usa pasa su etiqueta. */
export const searchThreshold = 6;

type Props = {
  id?: string;
  label: string;
  value: string;
  options: ChoiceOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  searchLabel?: string;
  clearSearchLabel?: string;
  searchIcon?: ReactNode;
  clearIcon?: ReactNode;
  describedBy?: string;
  invalid?: boolean;
  chevron?: ReactNode;
  check?: ReactNode;
  match?: (option: ChoiceOption, search: string) => boolean;
  placeholder?: string;
  /** K6 · C11 1.14.5: menú de valores cortos («Por página»): se abre debajo del botón, alineado a su borde izquierdo
   * (arriba solo si abajo no entra), con el ancho de su contenido, opciones de 40 px y el ✓ en su columna. */
  short?: boolean;
};

const fold = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export function ChoiceMenu(props: Props) {
  const generated = useId().replace(/:/g, "");
  const id = props.id ?? `choice-${generated}`;
  const menuId = `${id}-menu`;
  const [search, setSearch] = useState("");
  const menu = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const s = styles.scope;
  const showSearch = !!props.searchLabel && (props.options.length > searchThreshold || search.length > 0);
  const current = props.options.find((option) => option.value === props.value)?.label ?? props.placeholder ?? "—";
  const { options, match } = props;
  const visible = useMemo(
    () =>
      options.filter(
        (option) => search.length === 0 || (match ? match(option, search) : fold(option.label).includes(fold(search))),
      ),
    [options, match, search],
  );

  useEffect(() => {
    const element = menu.current;
    if (!element) return;
    // Al abrir con puntero fino en escritorio, el foco va al buscador o a la opción elegida; al cerrar, vuelve al botón.
    const onToggle = (event: Event) => {
      if ((event as ToggleEvent).newState === "open") {
        if (matchMedia("(pointer:fine)").matches && matchMedia("(min-width:768px)").matches)
          (
            element.querySelector<HTMLElement>("input") ??
            element.querySelector<HTMLElement>('[aria-checked="true"]') ??
            element.querySelector<HTMLElement>("button")
          )?.focus();
      } else trigger.current?.focus();
    };
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (event.key === "Escape" && target.matches("input[type=search]") && (target as HTMLInputElement).value) {
        event.preventDefault();
        event.stopPropagation();
        setSearch("");
        return;
      }
      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
      const items = [...element.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")];
      const index = items.indexOf(document.activeElement as HTMLButtonElement);
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? items.length - 1
            : (index + (event.key === "ArrowUp" ? -1 : 1) + items.length) % items.length;
      items[next]?.focus();
      event.preventDefault();
    };
    element.addEventListener("toggle", onToggle);
    element.addEventListener("keydown", onKey);
    return () => {
      element.removeEventListener("toggle", onToggle);
      element.removeEventListener("keydown", onKey);
    };
  }, []);

  const choose = (value: string) => {
    menu.current?.hidePopover();
    setSearch("");
    props.onChange(value);
  };

  return (
    <div className={`bh-choice ${s}`}>
      <button
        ref={trigger}
        id={id}
        type="button"
        className={`bh-choice-trigger ${s}`}
        disabled={props.disabled}
        popoverTarget={menuId}
        aria-label={props.label}
        aria-haspopup="menu"
        aria-describedby={props.describedBy}
        aria-invalid={props.invalid ? "true" : "false"}
      >
        <span className={s} data-empty={props.value ? undefined : "true"}>{current}</span>
        {props.chevron}
      </button>
      <div ref={menu} id={menuId} popover="auto" className={`bh-choice-menu ${props.short ? "bh-choice-short" : ""} ${s}`} role="menu" aria-label={props.label}>
        {showSearch ? (
          <div className={`bh-choice-search ${s}`}>
            {props.searchIcon}
            <input
              className={s}
              type="search"
              enterKeyHint="search"
              aria-label={props.searchLabel}
              placeholder={props.searchLabel}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search.length > 0 ? (
              <button
                type="button"
                className={`bh-choice-clear ${s}`}
                aria-label={props.clearSearchLabel}
                onClick={() => {
                  setSearch("");
                  menu.current?.querySelector<HTMLInputElement>("input[type=search]")?.focus();
                }}
              >
                {props.clearIcon}
              </button>
            ) : null}
          </div>
        ) : null}
        {visible.map((option) => (
          <button
            key={option.value}
            className={s}
            type="button"
            role="menuitemradio"
            aria-checked={props.value === option.value ? "true" : "false"}
            disabled={option.disabled}
            onClick={() => choose(option.value)}
          >
            <span className={s}>{option.label}</span>
            {props.value === option.value ? props.check : null}
          </button>
        ))}
      </div>
    </div>
  );
}
